/** Test Booster wizard types. Shapes mirror the API contract. */

export type TargetTest = 'act' | 'sat';

/** Which score sections show. 'none' = haven't taken a test yet. */
export type WhichTests = 'ACT' | 'SAT' | 'Both' | 'none';

/** Number of focus areas per study session. */
export type FocusPref = 'single' | 'double';

export type IntakeField = 'firstName' | 'expectedGraduationYear' | 'zip' | 'school' | 'whichTests';

export type StepKey = 'INTAKE' | 'SCORE_ACT' | 'SCORE_SAT' | 'TARGETS' | 'SCHEDULE';

export const SCHOOL_TIERS = ['reach', 'target', 'safety'] as const;

export type SchoolTier = (typeof SCHOOL_TIERS)[number];

/** One reporting-category subscore. ACT populates `percentCorrect` (0–100); SAT populates `valueLow`–`valueHigh`. */
export type Subscore = Readonly<{
  categoryCode: string;
  percentCorrect: number | null;
  valueLow: number | null;
  valueHigh: number | null;
}>;

export type SchoolSelection = Readonly<{
  institutionId: number | null;
  name: string;
}>;

/** A date the student can't study. `endDate` equals `startDate` for a single day. */
export type Blackout = Readonly<{
  id: string;
  startDate: string;
  endDate: string;
  reason?: string;
}>;

export type TestDate = Readonly<{
  test: TargetTest;
  /** ISO `YYYY-MM-DD`; formatted for display at the point of use. */
  date: string;
}>;

export type ActScores = Readonly<{
  composite: number | null;
  english: number | null;
  math: number | null;
  reading: number | null;
  science: number | null;
  writing: number | null;
}>;

export type SatScores = Readonly<{
  total: number | null;
  erw: number | null;
  math: number | null;
}>;

export type BoosterTargets = Readonly<{
  /** Null until the report resolves it: a "Both" taker's test is the stronger one, decided server-side. */
  targetTest: TargetTest | null;
  targetScore: number | null;
  testDate: string | null;
  /** The plan's calendar anchor. Null until picked, which reads as today. */
  startDate: string | null;
  hoursPerDay: number;
  focusPref: FocusPref;
  /** Weekday numbers a student can study, 0 (Sunday)–6 (Saturday). */
  studyDays: number[];
  schools: SchoolSelection[];
  blackouts: Blackout[];
}>;

export type BoosterDraft = Readonly<{
  firstName: string;
  /** The student's expected graduation year; back-filled onto the profile on save, or on the guest claim. */
  expectedGraduationYear: number | null;
  /** Year the student's current program started; back-filled onto the profile on save, or on the guest claim. */
  programStartYear: number | null;
  /** The student's own zip; back-filled onto the profile on save, or on the guest claim. */
  zip: string;
  /** The student's own school; back-filled onto the profile on save, or on the guest claim. */
  school: string;
  whichTests: WhichTests | null;
  /**
   * These three can hold a deselected test's data — scores survive a narrowed `whichTests`, so
   * read them through `testsInSelection`.
   */
  actScores: ActScores;
  satScores: SatScores;
  subscores: Subscore[];
  /**
   * Whether `subscores` is the whole breakdown. False whenever it may be a partial view — the
   * response didn't join it, or joined it with rows the local taxonomy couldn't place.
   */
  ownsSubscores: boolean;
  targets: BoosterTargets;
}>;

/** One reporting-category strand's diagnosis within a section. */
export type ReportStrandDiagnosis = Readonly<{
  strandCode: string;
  strandName: string;
  categoryLabel: string | null;
  /** Skills the student's current band demonstrates. */
  mastered: readonly string[];
  /** Skills that unlock at the next band up (empty when already at the top band). */
  growing: readonly string[];
}>;

/**
 * A school's reported mid-50% (25th–75th percentile) score range for one test, and the 50th
 * between them. `median` is null on its own: a school can report both bounds without the middle.
 */
export type SchoolMidRange = Readonly<{ low: number; median: number | null; high: number }>;

/**
 * Where a score falls in a school's mid-50% range. `in_range` is for a school reporting both
 * bounds but no median, where the half is unknowable.
 */
export type SchoolStanding = 'below' | 'lower_half' | 'in_range' | 'upper_half' | 'above';

/**
 * A selected school as the report tiers it: its reach/target/safety tier plus both tests'
 * reported mid-50% ranges (null when that test's data is absent), independent of the plan's test.
 */
export type ReportSchool = Readonly<{
  /** Null for a school with no linked institution; such a school has no profile to tier. */
  institutionId: number | null;
  name: string | null;
  tier: SchoolTier | null;
  actMid50: SchoolMidRange | null;
  satMid50: SchoolMidRange | null;
  /** The school's own page. Null when we hold no usable URL, so there is nothing to link. */
  siteUrl: string | null;
}>;

/** A mid-50% range as the wire sends it; every field is unvalidated. */
export type RawSchoolMidRange = Readonly<{
  low: number | null;
  median: number | null;
  high: number | null;
}>;

/** A tiered school before normalization; `tier` and both ranges are whatever the wire sent. */
export type RawReportSchool = Readonly<
  Omit<ReportSchool, 'tier' | 'actMid50' | 'satMid50' | 'siteUrl'> & {
    tier: string | null;
    actMid50: RawSchoolMidRange | null;
    satMid50: RawSchoolMidRange | null;
    siteUrl: string | null;
  }
>;

/** Per-section band diagnosis; band codes map to labels/colors at the UI layer. */
export type ReportSkillSection = Readonly<{
  section: string;
  sectionScore: number;
  band: string | null;
  benchmark: number;
  metBenchmark: boolean;
  nextBand: string | null;
  strands: readonly ReportStrandDiagnosis[];
}>;

/** Facts assembled by the compute endpoint; pure data, prose framing lives at the prompt layer. */
export type ReportFacts = Readonly<{
  test: TargetTest;
  currentScore: number;
  percentile: { value: number | null };
  concordance?: {
    strongerTest: TargetTest;
    convertedScore: number;
    act: { score: number; asSat: number };
    sat: { score: number; asAct: number };
  };
  /** Both null when no explicit target and no school-derived goal; pointsToGain 0 = goal met. */
  target: { score: number | null; pointsToGain: number | null };
  schools: readonly ReportSchool[];
  recommendedGoal: number | null;
  /** Omitted when there are no section scores or seeded standards to diagnose. */
  skills?: readonly ReportSkillSection[];
  focus?: unknown;
}>;

export type RawReportFacts = Readonly<
  Omit<ReportFacts, 'schools'> & { schools: readonly RawReportSchool[] }
>;

/** A school as the target-school analysis sees it: the plan test's range plus the decided facts. */
export type AnalyzedTargetSchool = Readonly<{
  name: string;
  tier: SchoolTier | null;
  /** The mid-50% for the plan's test only. Null when the school reports no range for it. */
  mid50: SchoolMidRange | null;
  /** The school's own page, offered when there is no range to show. Null when we hold none. */
  siteUrl: string | null;
  /** Where the current score falls; null whenever `mid50` is null. */
  standingNow: SchoolStanding | null;
  /** Where the goal would fall; null with no goal, or no range. */
  standingAtGoal: SchoolStanding | null;
  /** Points from the current score up to `mid50.low`; set only when `standingNow` is `below`. */
  pointsToLowNow: number | null;
  /** Points from the goal up to `mid50.low`; set only when `standingAtGoal` is `below`. */
  pointsToLowAtGoal: number | null;
  /** True only when both standings are known and differ. */
  goalChangesStanding: boolean;
}>;

/** The facts one target-school analysis is built from; every value arrives decided. */
export type TargetSchoolFacts = Readonly<{
  test: TargetTest;
  currentScore: number;
  /** The goal the at-goal fields were resolved against: the student's target, else our recommendation. */
  goalScore: number | null;
  goalIsRecommended: boolean;
  /** False once an analysis has already run. */
  isFirstAnalysis: boolean;
  /** Fit row missing because the read failed: `tier` and `mid50` are unknown, not unreported. */
  fitUnavailable: boolean;
  /** The oldest school still awaiting analysis; one analysis speaks about one school. */
  school: AnalyzedTargetSchool;
}>;

export type PlanDayKind = 'study' | 'rest' | 'off' | 'timed' | 'test_day';

/**
 * The engine-resolved study resource for an entry: a deep link into the exact prep unit, with a
 * display label and an optional caveat note (e.g. ACT skills studied via the SAT course).
 */
export type PlanResource = Readonly<{
  url: string;
  label: string;
  note: string | null;
  /** The provider's own clock; null where it runs none. */
  timing: ResourceTiming | null;
}>;

export type ResourceTiming = Readonly<{
  minutes: number;
  questionCount: number;
}>;

/** One section-colored entry on a plan day. `section` is the engine's raw label; the FE maps it to a color. */
export type PlanEntry = Readonly<{
  section: string;
  label: string;
  /** The entry's resolved resource; null when the engine emitted none. */
  resource: PlanResource | null;
}>;

/** A study target for the day: its reporting category, with the skill standard when one resolved. */
export type PlanTarget = Readonly<{
  section: string;
  category: string;
  /** Stable reporting-category code (e.g. `act_eng_pow`); null when the engine resolved none. */
  categoryCode: string | null;
  /** Null when the engine resolved no standard for the category. */
  skillText: string | null;
  /** The reporting category the day's quiz draws from; null until the question pipeline joins it. */
  scoreCategoryId: number | null;
  /** The target's resolved resource; null when the engine emitted none. */
  resource: PlanResource | null;
}>;

/** The day's latest recorded quiz attempt; gates the day into its results view. */
export type PlanDayAttempt = Readonly<{ score: number; total: number }>;

export type PlanDay = Readonly<{
  date: string;
  kind: PlanDayKind;
  completed: boolean;
  /** The latest recorded quiz attempt for the day; null when none has been recorded. */
  attempt: PlanDayAttempt | null;
  /** 1-based plan week. */
  week: number;
  entries: readonly PlanEntry[];
  targets: readonly PlanTarget[];
  /** Reason for an off (blackout) day, sourced from the submission's blackouts. */
  note?: string;
}>;

/** Headline plan numbers rendered as stat tiles. */
export type PlanStats = Readonly<{
  /** Null when no explicit target and no school-derived goal resolved; 0 = goal met. */
  pointsToGain: number | null;
  totalWeeks: number;
  hoursPerWeek: number;
}>;

/**
 * The engine's insufficient-schedule reason codes. Unknown codes are tolerated at the render
 * layer (a generic line), so consumers should accept a wider string when reading the API flag.
 */
export type InsufficientScheduleReason =
  | 'hours_per_week_below_minimum'
  | 'hours_per_week_critically_low'
  | 'fewer_than_minimum_weeks'
  | 'total_hours_below_minimum';

/**
 * The engine's non-blocking insufficient-schedule signal: a flag plus the reasons that raised it.
 * A warning, never a gate — the plan still renders and the student can always proceed.
 */
export type InsufficientSchedule = Readonly<{
  flag: boolean;
  reasons: readonly InsufficientScheduleReason[];
}>;

/**
 * Composite-band study profile that set how sharply the plan concentrates study time on weak
 * areas. Classified server-side from the composite; null for a non-finite score. Unknown future
 * codes are tolerated at the render layer (the profile card renders nothing rather than crashing).
 */
export type Archetype = 'floor_raiser' | 'mid_climber' | 'ceiling_raiser' | 'superscorer';

/**
 * Time-to-test phase that weighted the plan (nearer tests favor rules-based sections). Derived
 * server-side from the study window. Unknown future codes are tolerated at the render layer (the
 * profile card omits the phase chip rather than crashing).
 */
export type BoosterPhase = 'foundation' | 'standard' | 'sprint' | 'final';

/** One section's slice of study time; `share` is a whole percent and all shares sum to 100. */
export type PlanSection = Readonly<{
  section: string;
  share: number;
}>;

/** One section's current → projected band in a score projection. */
export type ProjectionSection = Readonly<{
  section: string;
  current: number;
  projected: number;
  /** Half-width of the 95% confidence band around `projected`. */
  ci95: number;
}>;

/** One point on the what-if curve: the projection at a given extra-weekly-hours level. */
export type ProjectionCurvePoint = Readonly<{
  extraHours: number;
  /** Total weekly study hours assumed at this point (base plus extra). */
  weeklyHours: number;
  composite: number;
  ci95: number;
  sections: readonly ProjectionSection[];
}>;

/**
 * Recompute-on-read projected test-day score with a visible confidence band and a
 * "what if I added X hrs/week" curve. Never persisted; null when the schedule and scores can't
 * support a projection, in which case the card renders nothing.
 */
export type BoosterProjection = Readonly<{
  /** Current composite (ACT) / total (SAT); null when unknown. */
  current: number | null;
  weeksRemaining: number;
  /** Base weekly study hours assumed (study days × hours/day, no extra). */
  weeklyHours: number;
  composite: number;
  ci95: number;
  sections: readonly ProjectionSection[];
  /** The projection sampled at extra weekly hours 0..10; the slider indexes into it. */
  curve: readonly ProjectionCurvePoint[];
}>;

/** The output-page study plan: the computed stats, calendar, and projection. */
export type BoosterPlan = Readonly<{
  startDate: string;
  testDate: string | null;
  stats: PlanStats;
  /** The engine's schedule-adequacy warning; null when the engine didn't emit one. */
  insufficientSchedule: InsufficientSchedule | null;
  /** The composite-band study profile; null when the engine didn't classify one. */
  archetype: Archetype | null;
  /** The time-to-test phase that weighted the plan; null when the engine didn't emit one. */
  phase: BoosterPhase | null;
  /** Per-section study-time allocation, in the engine's order; empty when none was emitted. */
  sections: readonly PlanSection[];
  days: readonly PlanDay[];
  /** Recompute-on-read projected test-day score; null when it can't be projected. */
  projection: BoosterProjection | null;
}>;

/** One answer option for a daily practice question. Exactly one option per question is correct. */
export type DailyQuestionOption = Readonly<{
  id: string;
  /** 1-based position; options render in this order. */
  sequence: number;
  text: string;
  isCorrect: boolean;
  /**
   * Why this option is right or wrong, in letter-free prose — the letter comes from position at
   * render. Null when the question carries only an overall explanation.
   */
  explanation: string | null;
  /** The option's own diagram, for items whose choices are drawn; null for text-only options. */
  mediaUrl: string | null;
}>;

/** One figure belonging to a question — a passage's stimulus, or an item's own diagram. */
export type DailyQuestionFigure = Readonly<{
  url: string;
  /** The caption the booklet prints ("Figure 2"); null where it numbers nothing. */
  label: string | null;
}>;

/**
 * One daily practice question. `passage` is present only for passage-based items, and `explanation`
 * only when there is an overall note beyond the per-option reasons. ACT Math items carry 5 options,
 * every other section 4.
 */
export type DailyQuestion = Readonly<{
  id: string;
  passage: string | null;
  /** The question's figures in printed order; empty for text-only items. */
  figures: readonly DailyQuestionFigure[];
  stem: string;
  explanation: string | null;
  /** The reporting-category strand the question assesses; null when untagged. */
  strandId: number | null;
  /** The strand's name; null when the question is untagged or the strand was not joined. */
  strandName: string | null;
  options: readonly DailyQuestionOption[];
}>;

/** Query args for a study day's fixed five practice questions (server-selected). */
export type QuestionsForDayParams = Readonly<{
  submissionId: string;
  dayDate: string;
}>;

/** One recorded per-question answer sent with a quiz attempt. Correctness and score are graded server-side. */
export type QuizAttemptAnswerInput = Readonly<{
  /** 0-based position in the presented quiz. */
  questionOrder: number;
  questionId: string;
  /** The stable id of the chosen option, or null when left blank. */
  chosenOptionId: string | null;
}>;

/** One recorded quiz attempt for a day. `score`/`total` are client-only (the optimistic overlay); the server derives the stored totals from the graded answers. */
export type QuizAttemptRequest = Readonly<{
  submissionId: string;
  dayDate: string;
  scoreCategoryId: number;
  score: number;
  total: number;
  /** Per-question detail; the server grades each choice and persists the result. */
  answers: readonly QuizAttemptAnswerInput[];
}>;

/** Query args for one day's recorded attempt answers (owner-scoped). */
export type QuizAttemptAnswersParams = Readonly<{
  submissionId: string;
  dayDate: string;
}>;

/** One persisted answer row returned by the attempt-answers read, with the question it embeds. */
export type QuizAttemptAnswer = Readonly<{
  questionOrder: number;
  questionId: string;
  chosenOptionIndex: number | null;
  isCorrect: boolean;
  /** The question answered, embedded so the results view renders without a bank fetch; null if it left the bank. */
  question: DailyQuestion | null;
}>;

/** The recorded attempt for a day plus its per-question answers (each embedding its question), in presented order. */
export type QuizAttemptAnswersResponse = Readonly<{
  score: number;
  total: number;
  scoreCategoryId: number;
  answers: readonly QuizAttemptAnswer[];
}>;

/** A study day's completion toggle. */
export type DayCompletionRequest = Readonly<{
  submissionId: string;
  dayDate: string;
  completed: boolean;
}>;

export type ComputeRequest = Readonly<{
  draft: BoosterDraft;
}>;

export type ComputeResponse = Readonly<{
  reportFacts: RawReportFacts;
  grade: string | null;
}>;

/** Tiering needs a score to compare against; `currentScore` is null before a test is entered. */
export type SchoolFitRequest = Readonly<{
  test: TargetTest;
  currentScore: number | null;
  institutionIds: readonly number[];
}>;

export type SchoolFitResponse = Readonly<{
  schools: readonly RawReportSchool[];
}>;

/** Phase-1 create: report-step draft plus the frozen report markdown; study plan not sent yet. */
export type CreateSubmissionRequest = Readonly<{
  draft: BoosterDraft;
  /** code → score_category_id map for the reporting-category breakdown. */
  categoryIdByCode: Record<string, number>;
  reportContent: string | null;
}>;

/** Phase-2 finalize: the created submission's id plus the completed draft (Targets land as the study plan). */
export type FinalizeSubmissionRequest = Readonly<{
  id: string;
  draft: BoosterDraft;
  categoryIdByCode: Record<string, number>;
  /**
   * Sent again at completion: scores stay editable after the report, so the row's copy can be stale. Omit when nothing was captured — null clears the stored report rather than skipping it.
   */
  reportContent?: string | null;
}>;

export type SubmissionResponse = Readonly<{ id: string }>;

/** The caller's most recent submission. `completedAt` null = created but not yet finalized. */
export type MostRecentSubmission = Readonly<{
  id: string;
  completedAt: string | null;
  reportContent: string | null;
  reportFacts: ReportFacts;
  /** The study plan; null until the calendar engine joins it onto the response. */
  plan: BoosterPlan | null;
  /** The saved inputs rebuilt into a draft so a resumed run can be edited (see restoreDraftFromSubmission). */
  restoredDraft: BoosterDraft;
}>;

/** One active reporting category from the reference endpoint. */
export type ScoreCategoryRef = Readonly<{
  id: number;
  code: string;
  test: TargetTest;
}>;
